import {Injectable, inject} from '@angular/core';
import {firstValueFrom} from 'rxjs';

import {TranslateService} from '@ngx-translate/core';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {CategoryPrefix} from '@common/utils/dist/models/models-pl2.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';
import {KeysPL2 as K} from '@common/utils/dist/models/keys-pl2.js';
import {
  DeviceConfigs,
  DeviceConfigUtils as DCU,
} from '@common/ble/dist/index.js';

import {RouteStore} from '@stores/route.store';
import {UserStore} from '@stores/user.store';
import {EntryStore} from '@stores/entry.store';
import {DataConfigStore} from '@stores/data-config.store';
import {ConnectionStore} from '@stores/connection.store';
import {AgentMemoryStore} from '@stores/agent-memory.store';
import {ActiveConnectionDataViewStore} from '@stores/active-connection-data-view.store';
import {WebsocketService} from '@services/websocket.service';
import {AttentionStore} from '@stores/attention.store';
import {LoadTrialPlotsAction} from '@actions/load-trial-plots.action';
import {cardDataWindow} from '@utils/attention-context-utils';

import {squashLesson} from '@utils/ai-utils';
import {buildChannelManifest} from '@utils/channel-manifest-utils';
import {LocalSubsetUtils as LSU} from '@utils/local-subset-utils';

// Sections the agent can focus on to analyse saved or live sensor data
const TRIAL_SECTION_TYPES: PL2.EntryType[] = [
  PL2.EntryType.CollectDataSection,
  PL2.EntryType.TrialSection,
];

interface TrialSectionRef {
  index: number;
  key: string;
  name: string;
}

/**
 * Trial and data cards the agent can focus on, by index into squashedLesson.data (which starts
 * with the lesson description).
 */
function trialSectionRefs(
  sortableItemsPKs: string[],
  entryState: ReturnType<EntryStore['state']>,
): TrialSectionRef[] {
  return sortableItemsPKs.flatMap((pK, i) => {
    const entry = entryState[pK]?.e;
    return entry && TRIAL_SECTION_TYPES.includes(entry.t as PL2.EntryType)
      ? [{index: i + 1, key: pK, name: entry.n ?? ''}]
      : [];
  });
}

@Injectable({providedIn: 'root'})
export class SendAgentMessageAction {
  private _routeStore = inject(RouteStore);
  private _userStore = inject(UserStore);
  private _entryStore = inject(EntryStore);
  private _dataConfigStore = inject(DataConfigStore);
  private _connectionStore = inject(ConnectionStore);
  private _memoryStore = inject(AgentMemoryStore);
  private _activeDataViewStore = inject(ActiveConnectionDataViewStore);
  private _websocketService = inject(WebsocketService);
  private _attentionStore = inject(AttentionStore);
  private _loadTrialPlots = inject(LoadTrialPlotsAction);
  private _translateService = inject(TranslateService);

  /**
   * Sends the user's message. `displayText` is what the chat shows for it when that differs
   * from the request the assistant gets (e.g. a chosen suggestion: "Spanish").
   */
  execute(
    chatSessionId: string,
    message: string,
    systemPromptOverride?: string,
    roleOverride?: 'student' | 'teacher',
    displayText?: string,
    lessonPK?: string,
  ) {
    const user = this._userStore.state();
    if (!user || !chatSessionId) return;

    this._memoryStore.addMessage(chatSessionId, {
      id: `${Date.now()}-user`,
      role: 'user',
      content: displayText ?? message,
      isStreaming: false,
    });

    // Create assistant placeholder
    const assistantId = `${Date.now()}-assistant`;
    this._memoryStore.addMessage(chatSessionId, {
      id: assistantId,
      role: 'assistant',
      content: '',
      isStreaming: true,
    });
    this._memoryStore.setStreaming(chatSessionId, true);

    const role = roleOverride ?? this._getRole();
    void this._send(
      {kind: 'message', chatSessionId, message},
      assistantId,
      role,
      systemPromptOverride,
      lessonPK,
    );
  }

  /**
   * Sends a teacher's message about a lesson other than the one on screen (e.g. one being
   * built in the background); the on-screen card focus and attention aren't included.
   */
  sendForLesson(
    chatSessionId: string,
    lessonPK: string,
    message: string,
    displayText?: string,
  ) {
    this.execute(
      chatSessionId,
      message,
      undefined,
      'teacher',
      displayText,
      lessonPK,
    );
  }

  /**
   * Continues a turn the server paused after an inline tool call (e.g. the agent recorded the
   * user's device). Fresh runtime context is sent, so the resumed turn sees the new device.
   */
  resume(
    chatSessionId: string,
    toolUseId: string,
    systemPromptOverride?: string,
    roleOverride?: 'student' | 'teacher',
    lessonPK?: string,
  ) {
    const user = this._userStore.state();
    if (!user || !chatSessionId) return;
    void this._send(
      {kind: 'resume', chatSessionId, toolUseId},
      `${Date.now()}-resume`,
      roleOverride ?? this._getRole(),
      systemPromptOverride,
      lessonPK,
    );
  }

  /** Registers an uploaded PDF with the conversation; the server tracks its extraction. */
  attachDocument(chatSessionId: string, key: string, name: string) {
    const user = this._userStore.state();
    if (!user || !chatSessionId) return;
    this._sendEvent(
      {kind: 'attach_document', chatSessionId, key, name},
      `${Date.now()}-attach`,
    );
  }

  /**
   * Sends the app's render of the current visualization at its check levels for the science
   * check; the result arrives as a visualization_check event.
   */
  checkVisualization(chatSessionId: string, vizKey: string, image: string) {
    const user = this._userStore.state();
    if (!user || !chatSessionId) return;
    this._sendEvent(
      {kind: 'visualization_check', chatSessionId, vizKey, image},
      `${Date.now()}-check`,
    );
  }

  /**
   * Sends the latest attention event (a device connected, a recording started or a trial was
   * saved) now, so the chat's focus moves without waiting for the next message.
   */
  sendAttention(chatSessionId: string) {
    const attention = this._attentionStore.state();
    if (!this._userStore.state() || !chatSessionId || !attention) return;
    this._sendEvent(
      {kind: 'focus_card', chatSessionId, context: {attention}},
      `${Date.now()}-focus`,
    );
  }

  /** Asks the server to push the persisted agent facts (e.g. a previously reported device). */
  hydrate(chatSessionId: string) {
    const user = this._userStore.state();
    if (!user || !chatSessionId) return;
    this._sendEvent({kind: 'hydrate', chatSessionId}, `${Date.now()}-hydrate`);
  }

  private async _send(
    request: Record<string, unknown>,
    eventId: string,
    role: 'student' | 'teacher' | 'admin',
    systemPromptOverride?: string,
    lessonPKOverride?: string,
  ) {
    const chatSessionId = request['chatSessionId'] as string;
    // Build context
    const lessonPK = (lessonPKOverride ??
      this._routeStore.state().containerId) as K.LabReportKey;
    // What's on screen (focused card, attention) belongs to another lesson
    const isOffScreen =
      !!lessonPKOverride &&
      lessonPKOverride !== this._routeStore.state().containerId;
    const entryState = this._entryStore.state();
    const lessonEntry = entryState[lessonPK]?.e;
    const sortableItems = LSU.retrieveAndSortLessonLikeEntries(
      lessonPK,
      entryState,
    );
    const sortableItemsPKs = sortableItems.map((item) => item.pK);

    // Squash the lesson for full context (include trial S3 locations for the agent tool)
    const squashedLesson = squashLesson(
      lessonPK,
      entryState,
      lessonEntry?.vN ?? '',
      lessonEntry?.mCats ?? [],
      '',
      sortableItemsPKs,
      {includeTrials: true},
    );

    // The server strips this before the model sees it
    const trialSections = trialSectionRefs(sortableItemsPKs, entryState);

    // The latest attention event moves the focus once: when the server hasn't applied it yet,
    // it does with this message. Otherwise the card the server says the conversation is about.
    const agentContext =
      this._memoryStore.getSession(chatSessionId)?.agentContext;
    const attention = isOffScreen ? null : this._attentionStore.state();
    const newAttention =
      request['kind'] === 'message' &&
      attention &&
      attention.at > (agentContext?.attentionAt ?? 0)
        ? attention
        : undefined;
    const cardPK = isOffScreen
      ? undefined
      : (newAttention?.key ?? agentContext?.focusedCard?.key);
    const activeCard = cardPK ? entryState[cardPK]?.e : null;
    const cardName = activeCard?.n ?? '';
    const focusedSection = trialSections.find((t) => t.key === cardPK);
    const activeCardContext = activeCard
      ? {
          cardName,
          cardType: activeCard.t ?? '',
          dataIndex: focusedSection?.index,
          isActiveContext: true,
          instructions:
            'This is the trial or data card the conversation is about. It corresponds to squashedLesson.data at the given dataIndex.',
        }
      : undefined;

    const trialDataWindow =
      cardPK && activeCard
        ? cardDataWindow({
            cardPK,
            card: activeCard,
            liveDataConfigState: this._dataConfigStore.state(),
            liveCardPK: this._activeDataViewStore.state(),
            trialPlots:
              activeCard.t === PL2.EntryType.TrialSection
                ? await firstValueFrom(
                    this._loadTrialPlots.execute(
                      (activeCard as PL2.TrialSectionEntry).pP,
                    ),
                  )
                : undefined,
            deviceName: this._connectionStore.state()?.deviceConfig?.name ?? '',
          })
        : null;
    console.debug(
      '[SendAgentMessage] focused card:',
      cardPK,
      activeCard?.t,
      'active data view:',
      this._activeDataViewStore.state(),
      'live points:',
      Object.values(this._dataConfigStore.state() ?? {}).map(
        (dvc) => `${dvc?.dS}:${dvc?.p?.length ?? 0}`,
      ),
      'trialDataWindow:',
      trialDataWindow ? trialDataWindow.state.status : 'null',
    );

    // Send full lesson context on every message. The squashed lesson is typically
    // 2-5k tokens — well within the 200k context window. This ensures the model
    // always has access to lesson structure, trial S3 locations, and section content.
    // The standards framework is added server-side from persisted facts.
    const learningContext = this._buildLearningContext(
      lessonEntry as PL2.LabReportEntry | undefined,
    );

    // Live device fact. The server resolves the effective device: connected, else the
    // device the user reported in the conversation.
    const connectedDevice = this._connectionStore.state()?.deviceConfig?.name;

    // authenticatedRole and device are resolved server-side
    const context: Record<string, unknown> = {
      lessonPK,
      squashedLesson,
      activeCard: activeCardContext,
      trialDataWindow: trialDataWindow ?? undefined,
      availableDevices: DCU.getDeviceNames(),
      ...(connectedDevice ? {connectedDevice} : {}),
      ...(learningContext ? {learningContext} : {}),
      ...(trialSections.length ? {trialSections} : {}),
      ...(attention ? {attention} : {}),
    };

    // Channel manifest for the same effective device (reported device comes from the
    // server's context projection)
    const manifestDeviceName =
      connectedDevice ?? this._memoryStore.getDeviceName(chatSessionId);

    if (manifestDeviceName) {
      const dc =
        this._connectionStore.state()?.deviceConfig ??
        DeviceConfigs.find((d) => d.name === manifestDeviceName);
      if (dc) {
        const translations = this._translateService.instant(
          'graph-config',
        ) as Record<string, string>;
        context.channelManifest = buildChannelManifest(
          manifestDeviceName,
          dc,
          translations,
        );
      }
    }

    // The server owns the system prompt; `mode` selects student/teacher instructions
    // (honoured only for teachers) and the override is a teacher-only experiment hook.
    const payload: Record<string, unknown> = {
      ...request,
      mode: role === 'student' ? 'student' : 'teacher',
      // Lesson search returns lessons in this language (English or Spanish)
      language:
        this._translateService.currentLang ??
        this._translateService.getBrowserLang(),
      context,
    };
    if (systemPromptOverride?.trim()) {
      payload.systemPromptOverride = systemPromptOverride;
    }

    this._sendEvent(payload, eventId);
  }

  private _sendEvent(payload: Record<string, unknown>, eventId: string) {
    const user = this._userStore.state();
    if (!user) return;

    // Send via WebSocket with the agent-invoke route
    const event: any = {
      wsR: APIM.Route.AgentInvoke,
      int: APIM.EventIntent.AgentInvoke,
      id: eventId,
      uId: user.userId,
      data: {
        user,
        ts: U.timestamp(),
        c: [payload],
      },
    };

    this._websocketService.sendRaw(event);
  }

  private _buildLearningContext(
    lessonEntry: PL2.LabReportEntry | undefined,
  ): Record<string, unknown> | null {
    const ctx: Record<string, unknown> = {};

    if (lessonEntry) {
      const cat = lessonEntry.cat;
      const extractTags = (prefix: CategoryPrefix): string[] =>
        cat
          ?.filter((c) => c.startsWith(prefix))
          .map((c) => c.substring(prefix.length)) ?? [];

      const grades = extractTags(CategoryPrefix.Grade);
      const alignments = extractTags(CategoryPrefix.Alignment);
      const sensors = extractTags(CategoryPrefix.Sensor);

      if (grades.length) ctx.gradeLevels = grades;
      if (alignments.length) ctx.standardAlignments = alignments;
      if (sensors.length) ctx.targetSensors = sensors;
      if (lessonEntry.dsc) ctx.lessonDescription = lessonEntry.dsc;
      if (lessonEntry.scC) ctx.curriculumCode = lessonEntry.scC;
    }

    return Object.keys(ctx).length ? ctx : null;
  }

  private _getRole(): 'student' | 'teacher' | 'admin' {
    const user = this._userStore.state();
    if (!user) return 'student';
    const role = (user as any).role;
    if (role === 'admin' || role === 'Admin') return 'admin';
    if (role === 'teacher' || role === 'Teacher') return 'teacher';
    return 'student';
  }
}
